import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import {
  EventType,
  EventTypeInput,
  createEventType,
  getEventType,
  updateEventType,
} from "@/features/supply-config/event-types-api";
import { getManagedEvents } from "@/features/supply-config/managed-events-api";
import { getTeams } from "@/features/supply-config/teams-api";
import useAllProfessionals from "@/features/professionals/hooks/use-all-professional";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { MultiSelect } from "@/features/forms/ui/bundles-multi-select";
import SchedulingDescriptionField, {
  DescriptionSection,
} from "@/features/supply-config/ui/scheduling-description-field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";

type Owner = "team" | "professional";

/** Lowercase + hyphenate free text into a stable slug (mirrors the config dialog). */
const slugify = (v: string) =>
  v
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const EMPTY: EventTypeInput = {
  managed_event_id: "",
  team_ids: [],
  professional_id: null,
  calendly_event_type_uri: "",
  scheduling_url: "",
  language: "",
  type: "",
  is_active: true,
};

export default function EventTypeForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: managedEvents = [] } = useQuery({
    queryKey: ["managed-events"],
    queryFn: getManagedEvents,
  });
  const { data: teams = [] } = useQuery({ queryKey: ["teams"], queryFn: getTeams });
  const { data: profData } = useAllProfessionals();
  const professionals = profData?.professionals ?? [];

  const { data: existing } = useQuery({
    queryKey: ["event-type", id],
    queryFn: () => getEventType(id!),
    enabled: isEdit,
  });

  const [owner, setOwner] = useState<Owner>("team");
  const [form, setForm] = useState<EventTypeInput>(EMPTY);
  const [descSections, setDescSections] = useState<DescriptionSection[]>([]);
  const [descTitle, setDescTitle] = useState("");
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (isEdit && existing && !hydrated) {
      setOwner(existing.professional_id ? "professional" : "team");
      setForm({
        managed_event_id: existing.managed_event_id,
        team_ids: existing.teams?.map((t) => t.id) ?? [],
        professional_id: existing.professional_id,
        calendly_event_type_uri: existing.calendly_event_type_uri ?? "",
        scheduling_url: existing.scheduling_url ?? "",
        language: existing.language ?? "",
        type: existing.type ?? "",
        is_active: existing.is_active,
        metadata: existing.metadata ?? undefined,
      });
      const sections = (existing.metadata as any)?.schedulingDescription;
      setDescSections(Array.isArray(sections) ? sections : []);
      const title = (existing.metadata as any)?.schedulingTitle;
      setDescTitle(typeof title === "string" ? title : "");
      setHydrated(true);
    }
  }, [isEdit, existing, hydrated]);

  const save = useMutation({
    mutationFn: (): Promise<EventType> => {
      const payload: EventTypeInput = {
        ...form,
        team_ids: owner === "team" ? form.team_ids : [],
        professional_id: owner === "professional" ? form.professional_id : null,
        language: form.language || null,
        // `type` applies to team round-robins only; clear it for individuals.
        type: owner === "team" ? slugify(form.type ?? "") || null : null,
        // Fold the scheduling override into metadata (preserving other keys). An
        // empty description/title parses to "no override" server-side, so the
        // booking page falls back to the managed event's.
        metadata: {
          ...(form.metadata ?? {}),
          schedulingTitle: descTitle.trim() || null,
          schedulingDescription: descSections
            .map((s) => ({
              subTitle: s.subTitle.trim(),
              content: (s.content ?? []).map((c) => c.trim()).filter(Boolean),
              ...(s.additionalConclusion?.trim()
                ? { additionalConclusion: s.additionalConclusion.trim() }
                : {}),
            }))
            .filter((s) => s.subTitle || s.content.length),
        },
      };
      return isEdit ? updateEventType(id!, payload) : createEventType(payload);
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["event-types"] });
      if (isEdit) qc.invalidateQueries({ queryKey: ["event-type", id] });
      toast({ title: isEdit ? "Event type updated" : "Event type created" });
      const targetId = isEdit ? id : res?.id;
      navigate(targetId ? `../${targetId}` : "..");
    },
    onError: (e: any) =>
      toast({
        title: "Error",
        description: e.response?.data?.message || "Failed",
        variant: "destructive",
      }),
  });

  return (
    <HasAccess permission="write">
      <div className="max-w-2xl mx-auto p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-semibold">
            {isEdit ? "Edit Event Type" : "New Event Type"}
          </h1>
        </div>

        <Card>
          <CardContent className="space-y-4 pt-6">
            <div>
              <p className="text-xs text-gray-500 mb-1">Managed event</p>
              <Select
                value={form.managed_event_id}
                onValueChange={(v) => setForm({ ...form, managed_event_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Managed event" />
                </SelectTrigger>
                <SelectContent>
                  {managedEvents.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">Mapped to</p>
              <div className="flex gap-2">
                {(["team", "professional"] as Owner[]).map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => setOwner(o)}
                    className={`px-3 py-1.5 rounded-md text-sm border ${
                      owner === o
                        ? "bg-sidebar text-white border-sidebar"
                        : "bg-white text-gray-600 border-gray-200"
                    }`}
                  >
                    {o === "team"
                      ? "Team round-robin"
                      : "Individual professional"}
                  </button>
                ))}
              </div>
            </div>

            {owner === "team" ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Teams</p>
                  <MultiSelect
                    options={teams.map((t) => ({ label: t.name, value: t.id }))}
                    value={form.team_ids ?? []}
                    onChange={(v) => setForm({ ...form, team_ids: v })}
                    placeholder="Select teams"
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Type (unique slug)</p>
                  <Input
                    placeholder="e.g. english"
                    value={form.type ?? ""}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    onBlur={(e) =>
                      setForm({ ...form, type: slugify(e.target.value) })
                    }
                  />
                </div>
              </div>
            ) : (
              <div>
                <p className="text-xs text-gray-500 mb-1">Professional</p>
                <Select
                  value={form.professional_id ?? ""}
                  onValueChange={(v) => setForm({ ...form, professional_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Professional" />
                  </SelectTrigger>
                  <SelectContent>
                    {professionals.map((p: any) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <p className="text-xs text-gray-500 mb-1">Calendly event type URI</p>
              <Input
                placeholder="https://api.calendly.com/event_types/…"
                value={form.calendly_event_type_uri ?? ""}
                onChange={(e) =>
                  setForm({ ...form, calendly_event_type_uri: e.target.value })
                }
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Scheduling URL</p>
              <Input
                placeholder="https://calendly.com/…"
                value={form.scheduling_url ?? ""}
                onChange={(e) =>
                  setForm({ ...form, scheduling_url: e.target.value })
                }
              />
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">
                Scheduling title{" "}
                <span className="text-gray-400">
                  (overrides the managed event's; falls back to it, then the
                  product name)
                </span>
              </p>
              <Input
                placeholder="e.g. Book your diagnostic session"
                value={descTitle}
                onChange={(e) => setDescTitle(e.target.value)}
              />
            </div>

            <SchedulingDescriptionField
              value={descSections}
              onChange={setDescSections}
            />

            <div className="flex items-center justify-between border rounded-md p-3">
              <span className="text-sm">Active</span>
              <Switch
                checked={form.is_active ?? true}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button
                onClick={() => save.mutate()}
                disabled={
                  save.isPending ||
                  !form.managed_event_id ||
                  (owner === "team"
                    ? !form.team_ids?.length
                    : !form.professional_id)
                }
              >
                {save.isPending ? "Saving…" : isEdit ? "Save changes" : "Create"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </HasAccess>
  );
}
